// Client API centralisé — utilise REACT_APP_BACKEND_URL et envoie les cookies.
//
// SEC-002 (P4) — Aucun JWT n'est stocké dans localStorage. Le cookie HttpOnly
// `session_token` posé par le backend est envoyé automatiquement grâce à
// `withCredentials: true`. Le refresh se fait via `/auth/refresh` (cookie
// `refresh_token` HttpOnly également).
import axios from "axios";
import offlineQueue from "@/lib/offlineQueue";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // cookie httpOnly session_token + refresh_token
});

// ---- Refresh token flow (transparent pour l'UI) --------------------------
// Sur 401, on tente un `/auth/refresh` (avec cookie refresh_token httpOnly),
// puis on rejoue la requête d'origine une seule fois. Le nouveau cookie
// `session_token` est posé par le backend — rien à mémoriser côté JS.
let refreshInFlight = null; // Promise partagée pour éviter les refresh en parallèle

async function attemptRefresh() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = axios
    .post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true })
    .then((r) => Boolean(r?.status === 200))
    .catch(() => false)
    .finally(() => {
      // Reset après un court délai pour permettre à une éventuelle 2e requête
      // parallèle de bénéficier du même refresh sans re-tenter.
      setTimeout(() => { refreshInFlight = null; }, 200);
    });
  return refreshInFlight;
}

// Interceptor de résilience réseau + refresh automatique.
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err?.config;
    const isNetworkError = !err.response;
    const isMutation = cfg && ["post", "put", "patch", "delete"].includes(
      (cfg.method || "").toLowerCase()
    );

    // --- 401 → tentative de refresh unique --------------------------------
    if (
      err?.response?.status === 401 &&
      cfg &&
      !cfg._retryAfterRefresh &&
      // Éviter la boucle : ne pas retry le call /auth/refresh lui-même
      !(cfg.url || "").includes("/auth/refresh") &&
      !(cfg.url || "").includes("/auth/login") &&
      !(cfg.url || "").includes("/auth/register")
    ) {
      cfg._retryAfterRefresh = true;
      const ok = await attemptRefresh();
      if (ok) {
        return api.request(cfg);
      }
    }

    // Ne pas enfiler les uploads multipart (fichiers non sérialisables ici)
    const isMultipart = (cfg?.headers?.["Content-Type"] || "")
      .toLowerCase().includes("multipart");
    if (isNetworkError && isMutation && !isMultipart && cfg?.url) {
      try {
        const relUrl = cfg.url.startsWith("http")
          ? cfg.url.replace(BACKEND_URL || "", "")
          : `/api${cfg.url.startsWith("/") ? cfg.url : "/" + cfg.url}`;
        await offlineQueue.enqueue({
          method: cfg.method,
          url: relUrl,
          data: cfg.data ? (typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data) : null,
          meta: { url: cfg.url },
        });
        return Promise.resolve({
          status: 202,
          data: { queued_offline: true },
          headers: {},
        });
      } catch (_) {
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
);

export default api;
export { BACKEND_URL };
